// Localização: frontend/src/components/Agendamentos/EventTooltip.jsx
// Componente de tooltip rico para exibir informações de agendamentos ao passar o mouse

import React from 'react';
import { Clock, User, Calendar, AlertCircle, CheckCircle, XCircle, PlayCircle } from 'lucide-react';
import moment from 'moment';
import { parseLocalDateTime } from '../../utils/formatters';

// Mapeamento de status para ícones
const statusIcons = {
  'Pendente': AlertCircle,
  'Em Andamento': PlayCircle,
  'Concluído': CheckCircle,
  'Cancelado': XCircle,
};

// Mapeamento de status para cores
const statusColors = {
  'Pendente': 'text-yellow-600',
  'Em Andamento': 'text-blue-600',
  'Concluído': 'text-green-600',
  'Cancelado': 'text-red-600',
};

const EventTooltip = ({ event, position, onEdit, onDelete }) => {
  if (!event || !event.resource) {
    return null;
  }

  const agendamento = event.resource;
  const StatusIcon = statusIcons[agendamento.status] || AlertCircle;
  const statusColor = statusColors[agendamento.status] || 'text-gray-600';

  // Formatar data e hora
  const startDate = parseLocalDateTime(agendamento.data_hora_inicio);
  const endDate = agendamento.data_hora_fim 
    ? parseLocalDateTime(agendamento.data_hora_fim)
    : null;

  // Debug: verificar se as datas foram parseadas corretamente
  if (process.env.NODE_ENV === 'development') {
    if (startDate && !isNaN(startDate.getTime())) {
      console.log('[EventTooltip] Data início parseada:', {
        original: agendamento.data_hora_inicio,
        parsed: startDate,
        formatted: moment(startDate).format('DD/MM/YYYY HH:mm')
      });
    }
    if (endDate && !isNaN(endDate.getTime())) {
      console.log('[EventTooltip] Data fim parseada:', {
        original: agendamento.data_hora_fim,
        parsed: endDate,
        formatted: moment(endDate).format('DD/MM/YYYY HH:mm')
      });
    }
  }

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return 'Data inválida';
    // Usar moment para formatar, garantindo que use a data local correta
    return moment(date).format('DD/MM/YYYY');
  };

  const formatTime = (date) => {
    if (!date || isNaN(date.getTime())) return 'Hora inválida';
    // Usar moment para formatar, garantindo que use a hora local correta
    return moment(date).format('HH:mm');
  };

  const formatTimeRange = () => {
    if (!startDate || isNaN(startDate.getTime())) return 'Horário inválido';
    if (endDate && !isNaN(endDate.getTime())) {
      return `${formatTime(startDate)} - ${formatTime(endDate)}`;
    }
    return formatTime(startDate);
  };

  // Calcular duração
  const duration = endDate && startDate && !isNaN(endDate.getTime()) && !isNaN(startDate.getTime())
    ? moment(endDate).diff(moment(startDate), 'minutes')
    : null;

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  // Truncar texto longo
  const truncate = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Estilo do tooltip baseado na posição
  const tooltipStyle = {
    position: 'fixed',
    left: position?.x || 0,
    top: position?.y || 0,
    zIndex: 1000,
    pointerEvents: 'none',
  };

  return (
    <div
      className="event-tooltip bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm animate-[slideIn_0.2s_ease]"
      style={tooltipStyle}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-base truncate">
            {agendamento.paciente_nome || 'Paciente não informado'}
          </h4>
          <p className="text-sm text-gray-600 mt-1 truncate">
            {agendamento.tipo_cuidado || agendamento.tipo || 'Agendamento'}
          </p>
        </div>
        <div className={`flex items-center gap-1 ml-2 ${statusColor}`}>
          <StatusIcon className="h-4 w-4" />
          <span className="text-xs font-semibold">{agendamento.status}</span>
        </div>
      </div>

      {/* Body */}
      <div className="space-y-2">
        {/* Data */}
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Calendar className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <span className="font-medium">{formatDate(startDate)}</span>
        </div>

        {/* Horário */}
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Clock className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <span className="font-medium">{formatTimeRange()}</span>
          {duration && (
            <span className="text-xs text-gray-500 ml-1">
              ({formatDuration(duration)})
            </span>
          )}
        </div>

        {/* Profissional */}
        {agendamento.profissional_nome && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <User className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <span className="truncate">{agendamento.profissional_nome}</span>
          </div>
        )}

        {/* Descrição */}
        {agendamento.descricao && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-600 italic line-clamp-2">
              {truncate(agendamento.descricao, 120)}
            </p>
          </div>
        )}
      </div>

      {/* Ações rápidas (se disponíveis) */}
      {(onEdit || onDelete) && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(event);
              }}
              className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 transition-colors pointer-events-auto"
            >
              Editar
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(event.id);
              }}
              className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors pointer-events-auto"
            >
              Cancelar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EventTooltip;

